import { watchReportExport } from './socket'
import { isTerminalExportSnapshot, waitForExportOnline } from './snapshotMerge'

export const REPORT_EXPORT_POLL_MS = 2000
export const REPORT_EXPORT_POLL_MAX_DELAY_MS = 30000
export const REPORT_EXPORT_TRACK_MAX_MS = 15 * 60 * 1000
export const REPORT_EXPORT_CONNECTED_POLL_MS = 120000

function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'))
      return
    }
    const onAbort = () => {
      window.clearTimeout(timer)
      reject(new DOMException('Aborted', 'AbortError'))
    }
    const timer = window.setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

/**
 * Tracks any report-export job using the shared Action Cable socket plus HTTP reconciliation.
 * Callers supply fetchStatus(); progress snapshots are merged with newestExportSnapshot rules.
 */
export async function trackReportExportJob({
  jobId,
  signal,
  realtimeEnabled,
  fetchStatus,
  onProgress,
  maxMs = REPORT_EXPORT_TRACK_MAX_MS,
  pollMs = REPORT_EXPORT_POLL_MS,
  pollMaxDelayMs = REPORT_EXPORT_POLL_MAX_DELAY_MS,
  connectedPollMs = REPORT_EXPORT_CONNECTED_POLL_MS,
  timeoutMessage = 'La descarga tardó demasiado. Intenta con un rango más corto.'
}) {
  const realtime = realtimeEnabled ? watchReportExport(jobId, signal) : null
  try {
    const started = Date.now()
    let pollDelay = pollMs
    const fetchWhenOnline = async () => {
      const online = await waitForExportOnline(signal, maxMs - (Date.now() - started))
      if (!online) throw new Error(timeoutMessage)
      return fetchStatus()
    }
    let statusPayload = await fetchWhenOnline()
    if (realtime) statusPayload = realtime.accept(statusPayload)

    while (Date.now() - started < maxMs) {
      signal.throwIfAborted()
      onProgress?.(statusPayload)
      if (isTerminalExportSnapshot(statusPayload)) return statusPayload

      const delay = Math.max(
        0,
        Math.min(realtime?.connected ? connectedPollMs : pollDelay, maxMs - (Date.now() - started))
      )
      const event = realtime ? await realtime.wait(delay) : await sleep(delay, signal)
      signal.throwIfAborted()
      if (Date.now() - started >= maxMs) break

      if (event) {
        statusPayload = realtime.accept(event)
      } else {
        const fetched = await fetchWhenOnline()
        statusPayload = realtime ? realtime.accept(fetched) : fetched
      }
      pollDelay = Math.min(pollDelay * 2, pollMaxDelayMs)
    }

    if (isTerminalExportSnapshot(statusPayload)) return statusPayload
    throw new Error(timeoutMessage)
  } finally {
    realtime?.close()
  }
}
